/**
 * patrol-building-search-edit.js
 */

import React from 'react';
import {
  Box,
  Grid,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { shouldRenderMobileLayout } from 'utils/app-context';
import useRecordStore from 'contexts/record-store';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import HiddenInput from 'components/form-inputs/hidden-input';
import CheckboxList from 'components/form-inputs/checkbox-list';
import TextField from 'components/form-inputs/text-field';
import Select from 'components/form-inputs/select';
import Autocomplete from 'components/form-inputs/autocomplete';
import Checkbox from 'components/form-inputs/checkbox';
import { PatrolTypeTitle } from '../../training-utils';


export default function PatrolBuildingSearchEdit(props) {
  const {
    storedRecord,
    patrolRecord,
    patrolIndex,
    patrolTypes,
    exerciseId,
    drawerState,
    translation,
    isExerciseEditor,
    options,
    hidden,
    defaultMonitor,
    formPrefix = '',
  } = props;
  const formNameBase = `${formPrefix}patrol.patrolTypes[${patrolIndex}].buildingSearch`;
  const typeRecord = patrolRecord?.buildingSearch;
  const recordStore = useRecordStore();
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, exerciseId);
  const formContext = useFormContext();
  const { setValue, getValues, resetField } = formContext;
  const { t } = useTranslation(translation);

  // Always have a value selected for areaSizeUnit. If e.g. ...partrolTypes[0].buildingSearch
  // was initialized to null then ...partrolTypes[0].buildingSearch.areaSizeUnit will
  // also be null in the form data, and the defaultValue won't automatically kick in
  // for anything other than undefined. In that case, reset the field declaratively.
  React.useEffect(() => {
    if (getValues(`${formNameBase}.areaSizeUnit`) === null) {
      resetField(`${formNameBase}.areaSizeUnit`, { defaultValue: typeRecord?.areaSizeUnit || options.indoorAreaUnits[0] });
    }
  }, [formNameBase, getValues, options.indoorAreaUnits, resetField, typeRecord?.areaSizeUnit]);

  // initialize form values
  React.useEffect(() => {
    if (!isExerciseEditor && !storedRecord.dateCompleted) {
      // get exercise record to populate shared field values
      if (exerciseRecord.get()) {
        const exerPatrolRecord = exerciseRecord.get().patrol.patrolTypes[patrolIndex].buildingSearch;
        setValue(`${formNameBase}.types`, exerPatrolRecord.types);
        setValue(`${formNameBase}.areaSize`, exerPatrolRecord.areaSize);
        setValue(`${formNameBase}.areaSizeUnit`, exerPatrolRecord.areaSizeUnit);
        setValue(`${formNameBase}.decoyCount`, exerPatrolRecord.decoyCount);
        setValue(`${formNameBase}.monitor`, exerciseRecord.get().monitor);
      }

      // set monitor to default value if provided
      if (defaultMonitor) {
        setValue(`${formNameBase}.monitor`, defaultMonitor)
      }
    }
  
  // exerciseRecord can't be in
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExerciseEditor, storedRecord.id, storedRecord.dateCompleted, patrolIndex, setValue]);


  const form = (
    <Box sx={{ display: hidden ? 'none' : undefined }}>
      {/* id */}
      <HiddenInput 
        name={`${formNameBase}.id`}
        defaultValue={typeRecord?.id || ''}
      />


      {/* monitor */}
      {!isExerciseEditor &&
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('buildingSearch.monitor.helpTitle')}
        text={t('buildingSearch.monitor.helpInfo')}
        style={{ marginTop: 8 }}
      >
        {focusHandlers =>
          <Grid container spacing={2}>
            <FormGridItem>
              <Autocomplete
                name={`${formNameBase}.monitor`}
                label={t('buildingSearch.monitor.label')}
                options={options.monitors.map(o => o.name)}
                focusHandlers={focusHandlers}
                defaultValue={typeRecord?.monitor || ''}
                rules={{
                  required: hidden ? undefined : t('buildingSearch.monitor.errMsgEmpty'),
                  validate: value => hidden ? true : value.trim() !== '' || t('buildingSearch.monitor.errMsgEmpty'),
                  maxLength: {
                    value: 100,
                    message: t('buildingSearch.monitor.errMsgLength')
                  }
                }}
              />
            </FormGridItem>
          </Grid>}
      </HelpTextItem>}


      {/* controlled negative */}
      {!isExerciseEditor &&
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('buildingSearch.controlledNegative.helpTitle')}
        text={t('buildingSearch.controlledNegative.helpInfo')}
      >
        <Checkbox
          name={`${formNameBase}.isControlledNegative`}
          label={t('buildingSearch.controlledNegative.label')}
          defaultValue={typeRecord?.isControlledNegative}
        />
      </HelpTextItem>}


      {/* building search types */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('buildingSearch.types.helpTitle')}
        text={t('buildingSearch.types.helpInfo')}
      >
        {focusHandlers =>
          <CheckboxList
            name={`${formNameBase}.types`}
            label={t('buildingSearch.types.label')}
            options={options.buildingSearchTypes}
            customOther
            focusHandlers={focusHandlers}
            defaultValue={typeRecord?.types || []}
            rules={{
              validate: hidden ? undefined : val => (val && val.length > 0) || t('buildingSearch.types.errMsgEmpty')
            }}
          />}
      </HelpTextItem>


      {/* area size */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('buildingSearch.areaSize.helpTitle')}
        text={t('buildingSearch.areaSize.helpInfo')}
        style={{ marginTop: 16 }}
      >
        {focusHandlers =>
          <>
            <Grid container spacing={2}>
              {/* area size input */}
              <FormGridItem xs={5} smd={6} smm={3} sm={3} lg={3}>     
                <TextField
                  name={`${formNameBase}.areaSize`}
                  label='Area Size'
                  type='number'
                  focusHandlers={focusHandlers}
                  defaultValue={typeRecord?.areaSize || ''}
                />
              </FormGridItem>
              {/* area size unit */}
              <FormGridItem xs={7} smd={6} smm={3} sm={4} lg={4}>
                <Select
                  name={`${formNameBase}.areaSizeUnit`}
                  options={options.indoorAreaUnits}
                  focusHandlers={focusHandlers}
                  defaultValue={typeRecord?.areaSizeUnit || options.indoorAreaUnits[0]}
                  style={{ marginTop: 4 }}
                  setValueAs={id => ({ id })}
                />
              </FormGridItem>
            </Grid>
          </>}
      </HelpTextItem>


      {/* decoy / quarry count */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('buildingSearch.decoyCount.helpTitle')}
        text={t('buildingSearch.decoyCount.helpInfo')}
        style={{ marginTop: 8 }}
      >
        {focusHandlers =>
          <Grid container spacing={2}>
            <FormGridItem xs={12} smd={6} smm={6} sm={6} md={4} lg={4}>     
              <TextField
                name={`${formNameBase}.decoyCount`}
                label={t('buildingSearch.decoyCount.label')}
                focusHandlers={focusHandlers}
                defaultValue={typeRecord?.decoyCount || ''}
                type="number"
                rules={{
                  required: hidden ? undefined : t('buildingSearch.decoyCount.errMsgEmpty'),
                  max: {
                    value: 999999999,
                    message: t('buildingSearch.decoyCount.errMsgSize')
                  },
                  pattern: {
                    value: /[0-9]+/,
                    message: t('buildingSearch.decoyCount.errMsgNumber')
                  },
                }}
              />
            </FormGridItem>
          </Grid>}
      </HelpTextItem>
    </Box>
  );


  return isExerciseEditor
    ? form
    : (
    <>
      <EditorIconSection
        iconName='patrol-building-search'
        iconMarginTop={0}
        helpProps={{
          translation,
          section: 'buildingSearch',
          suffix: patrolTypes.length > 1 ? ` (Scenario)` : '',
        }}
      >
        {!shouldRenderMobileLayout() &&
        <PatrolTypeTitle 
          patrolType={patrolRecord.type} 
          patrolTypes={patrolTypes} 
          step={patrolRecord.order}
        />}
        {form}
      </EditorIconSection>
    </>
  );
}